import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser, isImpersonating } from '@adornis/users/client/currentUser.js';
import { stopImpersonation } from '@adornis/users/client/impersonation.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { subscribeCurrentUserRolesForDisplay } from '@nsc/nexus-permissions/nexus-api/platform-gated-reads.js';
import { html, nothing } from 'lit';
export const NexusTopbar = stateful((props: { onLogoClick: () => void }) => {
  const user = useObservable(() => currentUser(), []);
  const impersonating = useObservable(() => isImpersonating, []);
  const { value: userRoles } = useQuery(
    () => subscribeCurrentUserRolesForDisplay()({ roleName: 1, permissions: 1 }),
    [user?._id],
  );

  return html`
    ${impersonating
      ? html`
          <div class="bg-warning text-warning-content px-4 py-2 flex items-center justify-between gap-3">
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-sm font-medium truncate">
                You are logged in as <strong>${user?.email ?? 'another user'}</strong> (Impersonation active)
              </span>
            </div>
            <button
              class="btn btn-sm btn-ghost shrink-0"
              @click=${async () => {
                await stopImpersonation();
                globalThis.location.assign('/');
              }}
            >
              End impersonation
            </button>
          </div>
        `
      : nothing}
    <nav class="navbar bg-base-100 shadow-md border-b border-neutral/10 sticky top-0 z-10 min-w-0">
      <div class="flex-1 px-3 sm:px-6 min-w-0 flex items-center gap-2">
        <button
          class="text-xl sm:text-2xl font-extrabold tracking-tight text-neutral bg-transparent border-0 p-0 shrink-0 cursor-pointer"
          @click=${props.onLogoClick}
        >
          nsc<span class="text-primary">}</span>
        </button>
        <span class="text-sm sm:text-lg font-semibold text-neutral/60 truncate">Nexus Dashboard</span>
      </div>
      <div class="px-3 sm:px-6 flex items-center gap-2 sm:gap-4 shrink-0 min-w-0">
        <div class="flex flex-wrap gap-1 justify-end max-w-40 sm:max-w-56 lg:max-w-xl">
          ${(userRoles ?? []).length > 0
            ? (userRoles ?? []).map(role => html`<span class="badge badge-primary badge-sm">${role.roleName}</span>`)
            : html`<span class="badge badge-ghost badge-sm">No role</span>`}
        </div>
        <span class="text-sm text-base-content/60 truncate max-w-36 sm:max-w-xs">${user?.email ?? ''}</span>
        <button class="btn btn-ghost btn-circle shrink-0" @click=${() => logout()} aria-label="Logout">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
        </button>
      </div>
    </nav>
  `;
});
